import { Credentials, CredentialsEntity, CredentialsStatus } from '../../../models/credentials';
import { getCredentials as getCredentialsImport } from '../../../lib-api/services/Credentials';

export type CredentialsPollerCallback = {
  onError: (credentials: CredentialsEntity) => void;
  onUpdated: (credentials: CredentialsEntity) => void;
  onAnyUpdate: (credentials: CredentialsEntity) => void;
};

export class CredentialsPoller {
  private stopped = true;

  constructor(private getCredentials = getCredentialsImport) {}

  private propagateUpdate(
    credentials: CredentialsEntity,
    credentialsPollerCallback: CredentialsPollerCallback
  ) {
    if (this.stopped) {
      return;
    }

    switch (credentials.status) {
      case CredentialsStatus.UPDATED:
        credentialsPollerCallback.onUpdated(credentials);
        break;
      case CredentialsStatus.AUTHENTICATION_ERROR:
      case CredentialsStatus.NOT_IMPLEMENTED_ERROR:
      case CredentialsStatus.TEMPORARY_ERROR:
        credentialsPollerCallback.onError(credentials);
        break;
      default:
        // In future in we need to track other credentials state, just add it here
        break;
    }

    credentialsPollerCallback.onAnyUpdate(credentials);
  }

  run(credentialCallbacks: CredentialsPollerCallback, credentialsId: string, statusUpdated = 0) {
    this.stopped = false;

    return new Promise((resolve) => {
      this.poll(credentialCallbacks, credentialsId, statusUpdated, resolve);
    });
  }

  stop() {
    this.stopped = true;
  }

  private async poll(
    credentialsPollerCallback: CredentialsPollerCallback,
    credentialsId: string,
    statusUpdated: number,
    resolve?: (credentials: CredentialsEntity) => void
  ) {
    const response = await this.getCredentials(credentialsId, true);
    const newCredentials = response.data;

    if (response.isOk() && newCredentials) {
      const oldTimestamp = Number(statusUpdated || 0);
      const newTimestamp = Number(newCredentials.statusUpdated || 0);

      const credentialsChanged =
        (oldTimestamp === 0 && newTimestamp === 0) || oldTimestamp < newTimestamp;

      if (credentialsChanged) {
        // We resolve the first set of new credentials for someone listening
        if (resolve) {
          resolve(newCredentials);
        }

        this.propagateUpdate(newCredentials, credentialsPollerCallback);
      }

      if (Credentials.hasError(newCredentials)) {
        this.propagateUpdate(newCredentials, credentialsPollerCallback);
      }

      setTimeout(() => {
        if (!this.stopped) {
          this.poll(
            credentialsPollerCallback,
            credentialsId,
            newTimestamp,
            credentialsChanged ? undefined : resolve
          );
        }
      }, 1000);
    }
  }
}
